import { useState } from "react";
import { api, downloadFile } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface Batch { id: number; reference: string; status: string; totalCents: number; invoiceCount: number }
interface Invoice { id: number; status: string; claimBatchId?: number | null }

const aud = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function ClaimBatches({ invoices, onChange }: { invoices: Invoice[]; onChange: () => void }) {
  const batches = useFetch<Batch[]>("/v1/billing/claim-batches");
  const [error, setError] = useState<string | null>(null);
  const draftCount = invoices.filter((i) => i.status === "draft").length;

  async function generate() {
    setError(null);
    try {
      await api("/v1/billing/claim-batches", { method: "POST", body: "{}" });
      batches.reload(); onChange();
    } catch (e) { setError((e as Error).message); }
  }
  async function reconcile(batch: Batch) {
    const claimed = invoices.filter((i) => i.claimBatchId === batch.id && i.status === "claimed");
    if (claimed.length === 0) return;
    await api(`/v1/billing/claim-batches/${batch.id}/reconcile`, { method: "POST", body: JSON.stringify({
      payments: claimed.map((i) => ({ invoiceId: i.id, status: "paid" })),
    }) });
    batches.reload(); onChange();
  }
  async function exportCsv(batch: Batch) {
    await downloadFile(`/v1/billing/claim-batches/${batch.id}/export.csv`, `${batch.reference}.csv`);
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-ink">PACE claims</h2>
        <button onClick={generate} disabled={draftCount === 0} className="btn-primary disabled:opacity-50">
          Generate claim ({draftCount} draft)
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {(batches.data ?? []).map((b) => (
        <div key={b.id} className="card p-4 flex flex-wrap items-center gap-3 justify-between">
          <div>
            <span className="font-mono text-sm text-ink">{b.reference}</span>
            <span className="text-xs text-mut ml-2">{b.invoiceCount} invoice{b.invoiceCount === 1 ? "" : "s"} · {aud(b.totalCents)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className={`chip ${b.status === "reconciled" ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"}`}>{b.status}</span>
            <button onClick={() => exportCsv(b)} className="text-sm text-teal-700 hover:underline">Export</button>
            {b.status !== "reconciled" && <button onClick={() => reconcile(b)} className="text-sm text-teal-700 hover:underline">Mark paid</button>}
          </div>
        </div>
      ))}
      {(batches.data ?? []).length === 0 && <p className="text-sm text-faint card p-5">No claim batches yet. Draft invoices are bundled into a PACE claim here.</p>}
    </section>
  );
}
